import { memo, useMemo, useState } from 'react';

import { faCalendarDay, faChevronDown, faChevronUp, faTimes } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';

import { useBoolean, useEvent } from 'core/hooks';
import { Button, ButtonVariants, Dropdown, Icon } from 'core/ui';
import { ComponentSizes } from 'core/ui/constants';

import {
  DATE_FIELD_OPTIONS,
  RELATIVE_DATE_FILTER_OPTIONS,
  RelativeDateFilterField,
  RelativeDateFilterType,
  getRelativeDateFilterDescription,
} from '../utils/relative-date-filters';

export type RelativeDateFilterProps = {
  selectedField: RelativeDateFilterField | null;
  selectedFilterType: RelativeDateFilterType | null;
  onFilterChange: (field: RelativeDateFilterField, filterType: RelativeDateFilterType) => void;
  onClearFilter: () => void;
  disabled?: boolean;
};

export const RelativeDateFilter = memo<RelativeDateFilterProps>(({ selectedField, selectedFilterType, onFilterChange, onClearFilter, disabled = false }) => {
  const [isExpanded, { toggle: toggleExpanded }] = useBoolean(false);
  const [tempField, setTempField] = useState<RelativeDateFilterField | null>(selectedField || 'dateCreated');
  const [tempFilterType, setTempFilterType] = useState<RelativeDateFilterType | null>(selectedFilterType);

  const hasActiveFilter = selectedField && selectedFilterType;

  const groupedFilterOptions = useMemo(() => {
    const groups = {
      relative: RELATIVE_DATE_FILTER_OPTIONS.filter((option) => option.category === 'relative'),
      periods: RELATIVE_DATE_FILTER_OPTIONS.filter((option) => option.category === 'periods'),
      ranges: RELATIVE_DATE_FILTER_OPTIONS.filter((option) => option.category === 'ranges'),
    };

    return [
      { header: 'Relative', options: groups.relative },
      { header: 'Periods', options: groups.periods },
      { header: 'Ranges', options: groups.ranges },
    ];
  }, []);

  const handleApplyFilter = useEvent(() => {
    if (tempField && tempFilterType) {
      onFilterChange(tempField, tempFilterType);
      toggleExpanded();
    }
  });

  const handleClearFilter = useEvent(() => {
    setTempField('dateCreated');
    setTempFilterType(null);
    onClearFilter();
  });

  const handleFieldChange = useEvent((field: RelativeDateFilterField) => {
    setTempField(field);
  });

  const handleFilterTypeChange = useEvent((filterType: RelativeDateFilterType) => {
    setTempFilterType(filterType);
  });

  const activeFilterDescription = hasActiveFilter ? getRelativeDateFilterDescription(selectedField, selectedFilterType) : null;

  return (
    <StyledContainer>
      <StyledHeader onClick={toggleExpanded} disabled={disabled}>
        <StyledHeaderContent>
          <StyledHeaderIcon icon={faCalendarDay} />
          <StyledHeaderText>{hasActiveFilter ? 'Date Filter' : 'Add Date Filter'}</StyledHeaderText>
        </StyledHeaderContent>
        <StyledHeaderActions>
          {hasActiveFilter && (
            <StyledClearButton
              icon={faTimes}
              onClick={(e) => {
                e.stopPropagation();
                handleClearFilter();
              }}
              title="Clear date filter"
            />
          )}
          <StyledExpandIcon icon={isExpanded ? faChevronUp : faChevronDown} />
        </StyledHeaderActions>
      </StyledHeader>

      {hasActiveFilter && !isExpanded && <StyledActiveFilter>{activeFilterDescription}</StyledActiveFilter>}

      {isExpanded && (
        <StyledContent>
          <StyledSection>
            <StyledSectionLabel>Date Field:</StyledSectionLabel>
            <StyledDropdown
              data={DATE_FIELD_OPTIONS}
              value={tempField}
              onChange={(e) => handleFieldChange(e.value)}
              textField="label"
              dataItemKey="field"
              valueField="field"
              title="Select date field"
              disabled={disabled}
            />
          </StyledSection>

          <StyledSection>
            <StyledSectionLabel>Time Period:</StyledSectionLabel>
            <StyledFilterGroups>
              {groupedFilterOptions.map((group) => (
                <StyledFilterGroup key={group.header}>
                  <StyledGroupHeader>{group.header}</StyledGroupHeader>
                  <StyledFilterOptions>
                    {group.options.map((option) => (
                      <StyledFilterOption
                        key={option.type}
                        $isSelected={tempFilterType === option.type}
                        onClick={() => handleFilterTypeChange(option.type)}
                        disabled={disabled}
                        title={option.description}
                      >
                        {option.label}
                      </StyledFilterOption>
                    ))}
                  </StyledFilterOptions>
                </StyledFilterGroup>
              ))}
            </StyledFilterGroups>
          </StyledSection>

          <StyledActions>
            <StyledActionButton
              onClick={handleApplyFilter}
              disabled={!tempField || !tempFilterType || disabled}
              variant={ButtonVariants.PRIMARY}
              size={ComponentSizes.SMALL}
            >
              Apply Filter
            </StyledActionButton>
            <StyledActionButton onClick={handleClearFilter} disabled={disabled} variant={ButtonVariants.SECONDARY} size={ComponentSizes.SMALL}>
              Clear
            </StyledActionButton>
          </StyledActions>
        </StyledContent>
      )}
    </StyledContainer>
  );
});

RelativeDateFilter.displayName = 'RelativeDateFilter';

const StyledContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3]};
  background: ${({ theme }) => theme.colors.palette.white};
`;

const StyledHeader = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  background: ${({ theme }) => theme.colors.palette.grayscale[1]};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ theme, disabled }) => (disabled ? theme.colors.palette.grayscale[1] : theme.colors.palette.grayscale[2])};
  }
`;

const StyledHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const StyledHeaderIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.palette.grayscale[6]};
  font-size: ${({ theme }) => theme.fontSizes.body};
`;

const StyledHeaderText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.body};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.palette.grayscale[8]};
`;

const StyledHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const StyledClearButton = styled(Icon)`
  color: ${({ theme }) => theme.colors.palette.grayscale[5]};
  cursor: pointer;
  padding: ${({ theme }) => theme.space.spacing10};
  border-radius: ${({ theme }) => theme.radii.base};
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.palette.reds[5]};
    background: ${({ theme }) => theme.colors.palette.grayscale[2]};
  }
`;

const StyledExpandIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.palette.grayscale[5]};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
`;

const StyledActiveFilter = styled.div`
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  color: ${({ theme }) => theme.colors.palette.grayscale[6]};
  background: ${({ theme }) => theme.colors.palette.grayscale[0]};
  border-top: 1px solid ${({ theme }) => theme.colors.palette.grayscale[2]};
`;

const StyledContent = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
  background: ${({ theme }) => theme.colors.palette.white};
`;

const StyledSection = styled.div`
  margin-bottom: ${({ theme }) => theme.space.spacing20};

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledSectionLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.palette.grayscale[7]};
  margin-bottom: ${({ theme }) => theme.space.spacing10};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StyledDropdown = styled(Dropdown)`
  width: 100%;

  & > div {
    height: 36px;
    font-size: ${({ theme }) => theme.fontSizes.body};
  }

  .k-dropdown-wrap {
    border-radius: ${({ theme }) => theme.radii.base};
    border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};

    &:hover {
      border-color: ${({ theme }) => theme.colors.palette.grayscale[5]};
    }

    &:focus-within {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
  }
`;

const StyledFilterGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const StyledFilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const StyledGroupHeader = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.palette.grayscale[6]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StyledFilterOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const StyledFilterOption = styled.button<{ $isSelected: boolean }>`
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing10};
  border: 1px solid ${({ theme, $isSelected }) => ($isSelected ? theme.colors.primary : theme.colors.palette.grayscale[4])};
  border-radius: ${({ theme }) => theme.radii.base};
  background: ${({ theme, $isSelected }) => ($isSelected ? theme.colors.primary : theme.colors.palette.white)};
  color: ${({ theme, $isSelected }) => ($isSelected ? theme.colors.palette.white : theme.colors.palette.grayscale[7])};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  font-weight: ${({ theme, $isSelected }) => ($isSelected ? theme.fontWeights.semiBold : theme.fontWeights.normal)};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: ${({ theme, $isSelected }) => ($isSelected ? theme.colors.primary : theme.colors.palette.grayscale[5])};
    background: ${({ theme, $isSelected }) => ($isSelected ? theme.colors.primary : theme.colors.palette.grayscale[1])};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing10};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.space.spacing20};
`;

const StyledActionButton = styled(Button)`
  min-width: 80px;
`;
